import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './css/guide.css';

const Guide = () => {
    const name = process.env.REACT_APP_APP_NAME || "ExamWorld";
    const [activeSection, setActiveSection] = useState('new-user');

    const sections = [
        {
            id: 'new-user',
            title: 'New Registration',
            icon: 'fa-solid fa-user-plus',
            steps: [
                'Click on the "Sign Up" link from the top navigation.',
                'Fill all required details in the registration form accurately.',
                'Click on the "Sign Up" button to submit.',
                'Check your email inbox for a verification link (Subject: "Verify Your Account").',
                'Click the verification link provided in your email.',
                'You will be automatically logged in after successful verification.'
            ]
        },
        {
            id: 'existing-user',
            title: 'User Login',
            icon: 'fa-solid fa-arrow-right-to-bracket',
            steps: [
                'Click on the "Sign In" link at the top.',
                'Enter your registered email address and password.',
                'Click the "Login" button.',
                'Forgot Password? Click the "Forgot Password" link below the form.',
                'Enter your registered email address and submit.',
                'Check your email for a password reset link.',
                'Click the link and securely set your new password.'
            ]
        },
        {
            id: 'select-course',
            title: 'Select Exams',
            icon: 'fa-solid fa-book',
            steps: [
                'After logging in, navigate to the "Courses" section.',
                'Browse through the available exams and test series.',
                'Select your preferred exam category.',
                'Review the details (Note: Some premium exams may require fees).',
                'Click "Add to My Exams" or "Enroll".',
                'Complete the secure payment process if required.'
            ]
        },
        {
            id: 'take-test',
            title: 'Take Mock Tests',
            icon: 'fa-solid fa-laptop-file',
            steps: [
                'Go to the "Test" section from the main dashboard.',
                'Select the specific mock test or practice set you wish to attempt.',
                'Read the exam instructions and marking scheme carefully before starting.',
                'Complete the test within the allocated time limit.',
                'Click "Submit" to finalize your answers.',
                'Review your performance analytics instantly.'
            ]
        },
        {
            id: 'profile-dashboard',
            title: 'Your Dashboard',
            icon: 'fa-solid fa-gauge',
            steps: [
                'Click on your Profile Icon or the "Profile" link.',
                'You will be redirected to your personal dashboard.',
                'Available features include:',
                '• View and update personal details.',
                '• Check enrolled courses and mock tests.',
                '• Analyze detailed test results and progress.',
                '• View transaction and payment history.',
                '• Manage account security and change passwords.'
            ]
        },
        {
            id: 'additional-help',
            title: 'Need Help?',
            icon: 'fa-solid fa-circle-question',
            steps: [
                'Visit the "Help Center" section in the footer for FAQs.',
                'Read the detailed "Guidelines" for platform rules.',
                'Check our Terms & Conditions for usage policies.',
                'Contact our support team directly for technical issues.',
                'Reach out via email or phone provided in the Contact section.'
            ]
        }
    ];

    const importantNotes = [
        'Always log in before attempting to access premium features or tests.',
        'Keep your login credentials strictly confidential. Account sharing is prohibited.',
        'Verify your email immediately after registration to avoid account suspension.',
        'Check your spam/junk folder if the verification email is not in your inbox.',
        'Use a stable internet connection while taking timed mock tests.',
        'Contact support immediately if you face any payment or technical issues.'
    ];

    return (
        <div className="guidePageWrapper">
            
            {/* Header Section */}
            <header className="guideHeaderContainer">
                <h2 className="guideTitle">Platform User Guide</h2>
                <p className="guideSubtitle">
                    Step-by-step instructions to help you navigate and use {name} effectively.
                </p>
            </header>

            {/* Introduction Alert */}
            <div className="guideAlertBox">
                <div className="alertIcon"><i className="fa-solid fa-circle-info"></i></div>
                <div className="alertContent">
                    <h4>Welcome to {name}</h4>
                    <p>
                        Our interface is designed to be intuitive and student-friendly. However, we recommend reviewing this guide to fully utilize all features. <strong>Important:</strong> You must be logged in to access tests and personal dashboards.
                    </p>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="guideTabsContainer">
                {sections.map((section) => (
                    <button
                        key={section.id}
                        className={`guideTabBtn ${activeSection === section.id ? 'activeTab' : ''}`}
                        onClick={() => setActiveSection(section.id)}
                    >
                        <i className={section.icon}></i> {section.title}
                    </button>
                ))}
            </div>

            {/* Main Content Area */}
            <main className="guideMainContent modernCard">
                {sections.map((section) => (
                    <div 
                        key={section.id}
                        className={`guideSection ${activeSection === section.id ? 'activeSection' : ''}`}
                    >
                        <div className="sectionTitleBox">
                            <h3><i className={`${section.icon} textPrimary`}></i> {section.title}</h3>
                        </div>
                        
                        <div className="guideStepsList">
                            {section.steps.map((step, index) => (
                                <div className="guideStepItem" key={index}>
                                    <div className="stepNumber">{index + 1}</div>
                                    <div className="stepText">{step}</div>
                                </div>
                            ))}
                        </div>

                        {/* Section-specific Actions */}
                        <div className="sectionActions">
                            {section.id === 'new-user' && (
                                <Link to="/signup" className="primaryActionBtn">Start Registration</Link>
                            )}
                            {section.id === 'existing-user' && (
                                <>
                                    <Link to="/login" className="primaryActionBtn">Go to Login</Link>
                                    <Link to="/forgetpassword" className="secondaryActionBtn">Forgot Password?</Link>
                                </>
                            )}
                            {section.id === 'select-course' && (
                                <Link to="/course" className="primaryActionBtn">Browse Courses</Link>
                            )}
                            {section.id === 'take-test' && (
                                <>
                                    <Link to="/test" className="primaryActionBtn">Go to Tests</Link>
                                    <Link to="/footer/manual" className="secondaryActionBtn">Read Exam Manual</Link>
                                </>
                            )}
                            {section.id === 'profile-dashboard' && (
                                <Link to="/profile" className="primaryActionBtn">Open Dashboard</Link>
                            )}
                            {section.id === 'additional-help' && (
                                <>
                                    <Link to="/footer/help" className="primaryActionBtn">Help Center</Link>
                                    <Link to="/footer/contact" className="secondaryActionBtn">Contact Support</Link>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </main>

            {/* Important Notes Grid */}
            <section className="guideNotesSection modernCard">
                <div className="sectionTitleBox">
                    <h3><i className="fa-solid fa-thumbtack textPrimary"></i> Important Notes</h3>
                </div>
                <div className="notesGrid">
                    {importantNotes.map((note, index) => (
                        <div className="noteCard" key={index}>
                            <i className="fa-solid fa-check textPrimary"></i>
                            <p>{note}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Support CTA */}
            <section className="guideSupportSection">
                <h3>Still have questions?</h3>
                <p>If you couldn't find the answer you were looking for, our support team is ready to assist you.</p>
                <Link to="/footer/contact" className="primaryActionBtn">Contact Support Team</Link>
            </section>

        </div>
    );
};

export default Guide;